import type { FileUploadError as FileUploadErrorType } from "@/utils/fileUploadErrorParser";

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@heroui/react";

export interface FileUploadErrorProps {
  /** Parsed error information */
  error: FileUploadErrorType;
  /** Callback for retry action */
  onRetry?: () => void;
  /** Callback for remove/dismiss action */
  onRemove?: () => void;
  /** Optional CSS class name */
  className?: string;
  /** Show retry button */
  showRetry?: boolean;
  /** Show remove button */
  showRemove?: boolean;
}

/**
 * File Upload Error Component
 *
 * Displays user-friendly error messages with collapsible technical details.
 * Follows STYLE-GUIDE.md patterns:
 * - Uses HeroUI components
 * - No text color overrides (let HeroUI handle theming)
 * - Responsive design
 */
export const FileUploadError: React.FC<FileUploadErrorProps> = ({
  error,
  onRetry,
  onRemove,
  className = "",
  showRetry = true,
  showRemove = true,
}) => {
  const { t } = useTranslation();
  const [showTechnicalDetails, setShowTechnicalDetails] = useState(false);

  return (
    <div
      className={`border-l-4 border-danger bg-danger-50 dark:bg-danger-50/10 p-4 rounded-r-lg ${className}`}
      data-testid="file-upload-error"
      role="alert"
    >
      {/* Error Icon and Message */}
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <svg
            aria-hidden="true"
            className="h-5 w-5 text-danger"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              clipRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              fillRule="evenodd"
            />
          </svg>
        </div>
        <div className="ml-3 flex-1">
          <h3 className="text-sm font-medium text-danger" data-testid="error-message">
            {t("fileUpload.uploadFailed", { defaultValue: "Upload Failed" })}
            {error.technicalDetails.fileName ? `: ${error.technicalDetails.fileName}` : ""}
          </h3>
          <div className="mt-2 text-sm" data-testid="error-description">
            {error.userMessage}
          </div>

          {/* Suggestions */}
          {error.suggestions && error.suggestions.length > 0 && (
            <div className="mt-3">
              <p className="text-sm font-medium">
                {t("fileUpload.suggestions", { defaultValue: "Suggestions:" })}
              </p>
              <ul className="mt-1 list-disc list-inside text-sm space-y-1">
                {error.suggestions.map((suggestion, index) => (
                  <li key={index}>{suggestion}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Technical Details Toggle */}
          <div className="mt-3">
            <button
              className="text-sm font-medium underline hover:no-underline focus:outline-none focus:ring-2 focus:ring-danger focus:ring-offset-2 rounded"
              data-testid="toggle-technical-details"
              type="button"
              onClick={() => setShowTechnicalDetails(!showTechnicalDetails)}
            >
              {showTechnicalDetails
                ? t("fileUpload.hideTechnicalDetails", { defaultValue: "Hide" })
                : t("fileUpload.showTechnicalDetails", { defaultValue: "Show" })}{" "}
              {t("fileUpload.technicalDetailsLabel", { defaultValue: "Technical Details" })}{" "}
              <span aria-hidden="true">{showTechnicalDetails ? "▲" : "▼"}</span>
            </button>
          </div>

          {/* Technical Details Content */}
          {showTechnicalDetails && (
            <div
              className="mt-3 p-3 bg-default-100 dark:bg-default-100/50 rounded text-xs font-mono space-y-1"
              data-testid="technical-details"
            >
              {error.technicalDetails.errorCode && (
                <div>
                  <span className="font-semibold">
                    {t("fileUpload.errorCode", { defaultValue: "Error Code:" })}
                  </span>{" "}
                  {error.technicalDetails.errorCode}
                </div>
              )}
              {error.technicalDetails.status && (
                <div>
                  <span className="font-semibold">
                    {t("fileUpload.status", { defaultValue: "Status:" })}
                  </span>{" "}
                  {error.technicalDetails.status}
                </div>
              )}
              {error.technicalDetails.fileName && (
                <div>
                  <span className="font-semibold">
                    {t("fileUpload.file", { defaultValue: "File:" })}
                  </span>{" "}
                  {error.technicalDetails.fileName}
                </div>
              )}
              {error.technicalDetails.fileSize && (
                <div>
                  <span className="font-semibold">
                    {t("fileUpload.size", { defaultValue: "Size:" })}
                  </span>{" "}
                  {error.technicalDetails.fileSize}
                </div>
              )}
              {error.technicalDetails.originalMessage && (
                <div>
                  <span className="font-semibold">
                    {t("fileUpload.message", { defaultValue: "Message:" })}
                  </span>{" "}
                  {error.technicalDetails.originalMessage}
                </div>
              )}
              <div>
                <span className="font-semibold">
                  {t("fileUpload.timestamp", { defaultValue: "Timestamp:" })}
                </span>{" "}
                {error.technicalDetails.timestamp}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          {(showRetry || showRemove) && (
            <div className="mt-4 flex gap-2">
              {showRetry && onRetry && (
                <Button
                  color="danger"
                  data-testid="retry-button"
                  size="sm"
                  variant="flat"
                  onClick={onRetry}
                >
                  {t("fileUpload.retryUpload", { defaultValue: "Retry Upload" })}
                </Button>
              )}
              {showRemove && onRemove && (
                <Button data-testid="remove-button" size="sm" variant="light" onClick={onRemove}>
                  {t("fileUpload.removeFile", { defaultValue: "Remove File" })}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
